import { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { posRestaurant } from '../api/posAPI';
import Layout from '../components/Layout';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(centavos) {
  return `₱${((centavos || 0) / 100).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtTime(date) {
  if (!date) return '—';
  return new Date(date).toLocaleTimeString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getDateRange(preset) {
  const now = new Date();
  const today = { dateFrom: new Date(now.setHours(0, 0, 0, 0)).toISOString(), dateTo: new Date(now.setHours(23, 59, 59, 999)).toISOString() };
  if (preset === 'today')     return today;
  if (preset === 'yesterday') {
    const y = new Date(); y.setDate(y.getDate() - 1);
    return { dateFrom: new Date(y.setHours(0, 0, 0, 0)).toISOString(), dateTo: new Date(y.setHours(23, 59, 59, 999)).toISOString() };
  }
  if (preset === 'week') {
    const start = new Date(); start.setDate(start.getDate() - start.getDay());
    return { dateFrom: new Date(start.setHours(0, 0, 0, 0)).toISOString(), dateTo: new Date().toISOString() };
  }
  if (preset === 'month') {
    const start = new Date(); start.setDate(1);
    return { dateFrom: new Date(start.setHours(0, 0, 0, 0)).toISOString(), dateTo: new Date().toISOString() };
  }
  return today;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  paid:      'bg-green-100 text-green-700',
  open:      'bg-blue-100 text-blue-700',
  billed:    'bg-amber-100 text-amber-700',
  split:     'bg-purple-100 text-purple-700',
  voided:    'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

const METHOD_STYLE = {
  cash:   'bg-green-50 text-green-700',
  card:   'bg-blue-50 text-blue-700',
  gcash:  'bg-sky-50 text-sky-700',
  maya:   'bg-indigo-50 text-indigo-700',
  charge: 'bg-orange-50 text-orange-700',
  free:   'bg-gray-100 text-gray-500',
};

// ─── Row expansion ────────────────────────────────────────────────────────────

function OrderRow({ order }) {
  const [open, setOpen] = useState(false);
  const activeItems = order.items?.filter(i => !i.voidedAt) || [];
  const voidedItems = order.items?.filter(i => i.voidedAt) || [];

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
        onClick={() => setOpen(v => !v)}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span className="font-mono font-medium text-gray-900 text-sm">{order.orderNo}</span>
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-gray-700">{order.tableName || '—'}</td>
        <td className="px-3 py-2.5 text-sm text-gray-700">{order.waiterName}</td>
        <td className="px-3 py-2.5 text-sm text-center text-gray-700">{order.pax || '—'}</td>
        <td className="px-3 py-2.5">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${order.type === 'dine-in' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'}`}>
            {order.type === 'dine-in' ? 'Dine-In' : 'Takeout'}
          </span>
        </td>
        <td className="px-3 py-2.5 text-sm text-center text-gray-600">{activeItems.length}</td>
        <td className="px-3 py-2.5 text-sm text-right text-gray-700">{fmt(order.subtotal)}</td>
        <td className="px-3 py-2.5 text-sm text-right text-gray-500">
          {order.discountApplied?.promoDiscount > 0 ? fmt(order.discountApplied.promoDiscount) : '—'}
        </td>
        <td className="px-3 py-2.5 text-sm text-right font-semibold text-gray-900">{fmt(order.totalAmount)}</td>
        <td className="px-3 py-2.5">
          {order.payment?.method ? (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${METHOD_STYLE[order.payment.method] || 'bg-gray-100 text-gray-600'}`}>
              {order.payment.method.toUpperCase()}
            </span>
          ) : '—'}
        </td>
        <td className="px-3 py-2.5">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[order.status] || 'bg-gray-100 text-gray-600'}`}>
            {order.status}
          </span>
        </td>
        <td className="px-3 py-2.5 text-xs text-gray-500">
          {fmtTime(order.payment?.paidAt || order.createdAt)}
        </td>
      </tr>

      {open && (
        <tr className="bg-gray-50 border-b border-gray-200">
          <td colSpan={12} className="px-8 py-3">
            <div className="max-w-xl">
              {activeItems.map(item => (
                <div key={item._id} className="flex justify-between py-1 text-sm text-gray-700">
                  <span>{item.quantity}× {item.name}</span>
                  <span className="font-medium">{fmt(item.subtotal)}</span>
                </div>
              ))}
              {voidedItems.map(item => (
                <div key={item._id} className="flex justify-between py-1 text-sm text-gray-400 line-through">
                  <span>{item.quantity}× {item.name} <span className="not-italic normal-case no-underline text-red-400 text-xs ml-1">VOID: {item.voidReason}</span></span>
                  <span>{fmt(item.subtotal)}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 mt-1 pt-1 space-y-0.5 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
                {order.discountApplied?.promoDiscount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>SC/PWD Discount</span><span>-{fmt(order.discountApplied.promoDiscount)}</span>
                  </div>
                )}
                {order.vatAmount > 0 && (
                  <div className="flex justify-between text-gray-600"><span>VAT (12%)</span><span>{fmt(order.vatAmount)}</span></div>
                )}
                <div className="flex justify-between font-bold text-gray-900 pt-0.5 border-t border-gray-200">
                  <span>TOTAL</span><span>{fmt(order.totalAmount)}</span>
                </div>
                {order.payment && (
                  <div className="text-xs text-gray-500 mt-1">
                    Paid via {order.payment.method?.toUpperCase()}
                    {order.payment.amountTendered > 0 && ` · Tendered: ${fmt(order.payment.amountTendered)}`}
                    {order.payment.change > 0 && ` · Change: ${fmt(order.payment.change)}`}
                    {order.payment.referenceNo && ` · Ref: ${order.payment.referenceNo}`}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm px-5 py-4">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week',      label: 'This Week' },
  { key: 'month',     label: 'This Month' },
];

const STATUS_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'paid',      label: 'Paid' },
  { key: 'billed',    label: 'Billed' },
  { key: 'voided',    label: 'Voided' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function RestaurantSalesPage() {
  const { getAccessToken } = useAuth();

  const [datePreset, setDatePreset] = useState('today');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);

  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const filters = useMemo(() => ({
    ...dateRange,
    status,
    search: search.trim() || undefined,
    page,
    limit: 50,
  }), [dateRange, status, search, page]);

  const fetchData = useCallback(async () => {
    setIsFetching(true);
    if (!result) setIsLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const res = await posRestaurant.getSalesOrders(token, filters);
      setResult(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [filters, getAccessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const orders     = result?.data?.orders     || [];
  const summary    = result?.data?.summary    || {};
  const pagination = result?.data?.pagination || {};

  const handleDatePreset = (key) => {
    setDatePreset(key);
    setPage(1);
  };

  const handleStatus = (key) => {
    setStatus(key);
    setPage(1);
  };

  return (
    <Layout pageTitle="Restaurant Sales">
    <div className="h-full flex flex-col bg-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="font-bold text-gray-900 text-lg">Restaurant Sales</h1>
          <button
            onClick={() => fetchData()}
            disabled={isFetching}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {/* Date presets */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {DATE_PRESETS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleDatePreset(key)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  datePreset === key ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search order #…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 mt-3 border-b border-gray-200 -mb-px">
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleStatus(key)}
              className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
                status === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <SummaryCard
            label="Gross Sales"
            value={fmt(summary.grossSales)}
            sub={`${summary.totalTransactions ?? 0} transactions`}
          />
          <SummaryCard
            label="Net Sales"
            value={fmt(summary.netSales)}
          />
          <SummaryCard
            label="Discounts"
            value={fmt(summary.totalDiscount)}
          />
          <SummaryCard
            label="VAT Collected"
            value={fmt(summary.totalVAT)}
          />
        </div>

        {/* Orders table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {error ? (
            <div className="p-8 text-center text-red-600 text-sm">
              {error.response?.data?.message || error.message || 'Failed to load orders'}
            </div>
          ) : isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No orders found for this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5">Order #</th>
                    <th className="px-3 py-2.5">Table</th>
                    <th className="px-3 py-2.5">Waiter</th>
                    <th className="px-3 py-2.5 text-center">Pax</th>
                    <th className="px-3 py-2.5">Type</th>
                    <th className="px-3 py-2.5 text-center">Items</th>
                    <th className="px-3 py-2.5 text-right">Subtotal</th>
                    <th className="px-3 py-2.5 text-right">Discount</th>
                    <th className="px-3 py-2.5 text-right">Total</th>
                    <th className="px-3 py-2.5">Payment</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <OrderRow key={order._id} order={order} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <span className="text-xs text-gray-500">
                Page {pagination.page} of {pagination.pages} · {pagination.total} orders
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page >= pagination.pages}
                  className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </Layout>
  );
}
